import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { HugeiconsIcon } from '@hugeicons/react'
import { FloppyDiskIcon } from '@hugeicons-pro/core-bulk-rounded'
import { Box, Checkbox, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material'
import PropTypes from 'prop-types'

import { useGetSecondaryProcessesQuery } from '@/app/services/secondaryProcesses'
import { addItemToUpdateQueue, removeItemFromUpdateQueue } from '@/app/slices/orderItemsSlice'
import { selectOrganisationId } from '@/app/slices/organisationSlice'

import SecondaryProcessRow from './SecondaryProcessRow'

const SecondaryProcessesModal = ({
    accentColor = 'secondary',
    foldingEnabled = false,
    isRotary,
    onSaveClose,
    open,
    orderItemId,
    secondaryProcesses,
    showFolding,
}) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const organisationId = useSelector(selectOrganisationId)
    const { data: organisationSecondaryProcesses } = useGetSecondaryProcessesQuery({ organisationId })

    const [enabledProcesses, setEnabledProcesses] = useState(secondaryProcesses || [])
    const [isFoldingVisible, setIsFoldingVisible] = useState(showFolding)

    const onChangeFolding = (event) => {
        const { checked } = event.target
        setIsFoldingVisible(checked)
        //folding is not a secondary process, but we need to save it in the same way
        if (checked) {
            const foldingProcess = {
                secondaryProcessId: 'Folding',
                name: 'Folding',
                isEnabled: true,
            }
            setEnabledProcesses([...enabledProcesses, foldingProcess])
            dispatch(addItemToUpdateQueue(orderItemId))
        }
        if (!checked) {
            setEnabledProcesses(enabledProcesses.filter((process) => process.secondaryProcessId !== 'Folding'))
            dispatch(removeItemFromUpdateQueue(orderItemId))
        }
    }

    const onCheckboxChange = (event) => {
        const { checked, value } = event.target
        if (checked) {
            const processToEnable = organisationSecondaryProcesses.find(
                (process) => process.secondaryProcessId === value
            )
            setEnabledProcesses([...enabledProcesses, processToEnable])
        } else {
            setEnabledProcesses(enabledProcesses.filter((process) => process.secondaryProcessId !== value))
        }
    }

    const formattedProcesses = useMemo(() => {
        const newProcessList = enabledProcesses.map((process) => {
            //hanlde folding process
            if (process.secondaryProcessId === 'Folding') {
                return {
                    ...process,
                    isEnabled: true,
                }
            }
            //handle other processes
            const { $type, ...cleanProcess } = process
            return {
                ...cleanProcess,
                isEnabled: true,
                input:
                    organisationSecondaryProcesses.find((p) => p.secondaryProcessId === process.secondaryProcessId)
                        .measureType === 'Quantity'
                        ? process.input || 1
                        : 0,
                customValue: organisationSecondaryProcesses.find(
                    (p) => p.secondaryProcessId === process.secondaryProcessId
                )?.customPropertyName
                    ? process.customValue
                    : '',
            }
        })

        // if isFoldingVisible but enabledProcesses don have it, add it anyway to newprocessList
        if (isFoldingVisible && !enabledProcesses.some((process) => process.secondaryProcessId === 'Folding')) {
            const foldingProcess = {
                secondaryProcessId: 'Folding',
                name: 'Folding',
                isEnabled: true,
            }
            newProcessList.push(foldingProcess)
        }

        return [...newProcessList]
    }, [enabledProcesses])

    const filteredProcesses = useMemo(() => {
        return organisationSecondaryProcesses.filter(
            (process) => !process.isDeleted && process.webStoreEnabled && process.customerVisible
        )
    }, [organisationSecondaryProcesses])

    return (
        <Dialog
            open={open}
            slotProps={{
                paper: {
                    sx: { width: 380 },
                },
            }}
        >
            <DialogTitle
                alignItems="center"
                data-testid="order-item-secondary-processes-modal"
                display="flex"
                justifyContent="space-between"
            >
                <Typography variant="strong1">{t('SECONDARY PROCESSES')}</Typography>
                <IconButton
                    color={accentColor}
                    data-testid="order-item-secondary-process-save-button"
                    onClick={() => onSaveClose(formattedProcesses)}
                >
                    <HugeiconsIcon icon={FloppyDiskIcon} />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <>
                    <Typography variant="body2">
                        {t('Select the secondary processes you want to use in this part.')}
                    </Typography>
                    <Box sx={{ mt: 3 }}>
                        {!isRotary && (foldingEnabled || showFolding) ? (
                            <SecondaryProcessRow key="folding-row">
                                <Checkbox
                                    checked={isFoldingVisible}
                                    color={accentColor}
                                    data-testid="secondary-process-option-checkbox"
                                    size="small"
                                    onChange={onChangeFolding}
                                />
                                <Typography data-testid="secondary-process-option-label">{t('Folding')}</Typography>
                            </SecondaryProcessRow>
                        ) : null}

                        {filteredProcesses.length >= 1
                            ? filteredProcesses.map((process) => (
                                  <SecondaryProcessRow key={process.secondaryProcessId}>
                                      <Checkbox
                                          checked={enabledProcesses.some(
                                              (ep) => ep.secondaryProcessId === process.secondaryProcessId
                                          )}
                                          color={accentColor}
                                          data-testid="secondary-process-option-checkbox"
                                          size="small"
                                          value={process.secondaryProcessId}
                                          onChange={onCheckboxChange}
                                      />
                                      <Typography data-testid="secondary-process-option-label">
                                          {process.name}
                                      </Typography>
                                  </SecondaryProcessRow>
                              ))
                            : null}
                    </Box>
                </>
            </DialogContent>
        </Dialog>
    )
}

SecondaryProcessesModal.propTypes = {
    accentColor: PropTypes.string,
    foldingEnabled: PropTypes.bool,
    isRotary: PropTypes.bool,
    open: PropTypes.bool,
    orderItemId: PropTypes.string,
    primaryColor: PropTypes.string,
    secondaryProcesses: PropTypes.array,
    showFolding: PropTypes.bool,
    onSaveClose: PropTypes.func,
}

export default SecondaryProcessesModal
